/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import AppLoader from "../../components/AppLoader";
import { usePageLoader } from "../../services/usePageLoader";
import { useConfirmDialog } from "../../services/useConfirmDialog";
import MesEmptyState from "../../components/mes-empty-state/mes-empty-state";
import {
  addList,
  getList,
  getListById,
  updateById,
} from "../../services/services";
import { ENDPOINT } from "../../services/api-endpoint";
import "./mes-operations.scss";

function toggleIdInSet(prev: Set<number>, id: number): Set<number> {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

type WorkflowUsage = { id: number; workflowname: string };

type ProcessRow = {
  id: number;
  operationname?: string | null;
  workcenter?: string | null;
  workcentername?: string | null;
  machineTypeName?: string | null;
  catalogTechnologyName?: string | null;
  catalogModelName?: string | null;
  catalogBrandName?: string | null;
  mfgTechnologyName?: string | null;
  iotdevicecode?: string | null;
  operatorNames?: string | null;
  workflowUsages?: WorkflowUsage[];
  workflowCount?: number;
  active?: boolean;
};

type WorkRow = {
  id: number;
  workcentername?: string | null;
  machineTypeName?: string | null;
  catalogTechnologyName?: string | null;
  catalogModelName?: string | null;
  catalogBrandName?: string | null;
};

type OperatorRow = {
  id: number;
  fullname?: string | null;
  skills?: string | null;
};

function formatOperatorSkills(raw: string | null | undefined): string {
  const parts = String(raw ?? "")
    .split(/[,;]/g)
    .map((s) => s.trim())
    .filter(Boolean);
  if (!parts.length) return "";
  return `— ${parts.join(", ")}`;
}

function formatMachineCatalogSubtitle(row: {
  catalogBrandName?: string | null;
  catalogModelName?: string | null;
  machineTypeName?: string | null;
}): string {
  const brand = (row.catalogBrandName ?? "").trim();
  const model =
    (row.catalogModelName ?? "").trim() ||
    (row.machineTypeName ?? "").trim();
  if (brand && model) return `${brand} · ${model}`;
  return model || brand || "—";
}

function normalizeEditRow(raw: unknown): {
  operationname: string;
  workcenter: string;
  assigneeUserIds: number[];
} | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = Number(o.id);
  if (!Number.isFinite(id)) return null;
  const assigneeRaw = o.assigneeUserIds;
  const assigneeUserIds = Array.isArray(assigneeRaw)
    ? assigneeRaw.map((x) => Number(x)).filter((n) => Number.isFinite(n))
    : [];
  return {
    operationname: String(o.operationname ?? "").trim(),
    workcenter: o.workcenter != null ? String(o.workcenter) : "",
    assigneeUserIds,
  };
}

const MesOperation = () => {
  const [validated, setValidated] = useState(false);
  const [formInputs, setForm] = useState({
    operationname: "",
    workcenter: "",
  });
  const [selectedOperatorIds, setSelectedOperatorIds] = useState<number[]>([]);
  const [editId, setEditId] = useState<number | "">("");
  const [workCenterList, setWorkCenterList] = useState<WorkRow[]>([]);
  const [operatorList, setOperatorList] = useState<OperatorRow[]>([]);
  const [operationCollection, setOperation] = useState<ProcessRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [openAddUpdateModal, setAddUpdateModal] = useState<boolean>(false);
  /** Name column: workflow names + machine details — collapsed by default (mes-app-v2). */
  const [expandedNameRowIds, setExpandedNameRowIds] = useState<Set<number>>(
    () => new Set(),
  );
  const { isLoading, runWithLoading } = usePageLoader();
  const [busyLabel, setBusyLabel] = useState("Loading process definitions…");
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const searchDebounceRef = useRef<number>();

  const handleClose = () => {
    setValidated(false);
    setEditId("");
    setForm({ operationname: "", workcenter: "" });
    setSelectedOperatorIds([]);
    setAddUpdateModal(false);
  };

  const loadModalReferences = useCallback(async () => {
    const [workList, ops] = await Promise.all([
      getList(ENDPOINT.WORK_CENTER_LIST),
      getList(ENDPOINT.OPERATOR_LIST, {
        pagesize: 500,
        pagenumber: 0,
        serachtext: "",
      }),
    ]);
    setWorkCenterList(Array.isArray(workList) ? (workList as WorkRow[]) : []);
    const opItems = Array.isArray(ops) ? ops : (ops as { data?: OperatorRow[] })?.data || [];
    setOperatorList(opItems as OperatorRow[]);
  }, []);

  const handleShowCreate = () => {
    setValidated(false);
    setEditId("");
    setForm({ operationname: "", workcenter: "" });
    setSelectedOperatorIds([]);
    setBusyLabel("Loading form…");
    void runWithLoading(async () => {
      await loadModalReferences();
      setBusyLabel("Loading process definitions…");
      setAddUpdateModal(true);
    });
  };

  const handleFieldChange = (name: "operationname" | "workcenter", value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleOperator = (operatorId: number) => {
    setSelectedOperatorIds((prev) => {
      if (prev.includes(operatorId)) {
        return prev.filter((x) => x !== operatorId);
      }
      return [...prev, operatorId];
    });
  };

  const onSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    const creating = editId === "";
    setBusyLabel(creating ? "Saving process definition…" : "Updating process definition…");
    try {
      await runWithLoading(async () => {
        const assignees = selectedOperatorIds.map((userid) => ({
          userid,
          active: true,
        }));
        const base = {
          operationname: formInputs.operationname.trim(),
          workcenter: Number(formInputs.workcenter),
          active: true,
          assignees,
        };
        let status: unknown;
        if (creating) {
          status = await addList(ENDPOINT.OPERATIONS_ADD, base);
        } else {
          status = await updateById(ENDPOINT.OPERATIONS_UPDATE, {
            ...base,
            id: editId,
          });
        }
        if (status === "success") {
          toast.success(
            creating
              ? "Process definition created successfully."
              : "Process definition updated successfully.",
          );
          handleClose();
          await onLoad(creating ? 0 : pageIndex, searchText);
        } else {
          toast.error(
            creating
              ? "Could not create process definition."
              : "Could not update process definition.",
          );
        }
      });
    } catch (e) {
      console.log(`Error saving process definition: ${e}`);
      toast.error("Could not save process definition.");
    } finally {
      setBusyLabel("Loading process definitions…");
    }
  };

  const onEdit = async (row: ProcessRow) => {
    setBusyLabel("Loading…");
    try {
      await runWithLoading(async () => {
        await loadModalReferences();
        const raw = await getListById(ENDPOINT.OPERATIONS_LIST_BY_ID, row.id);
        const normalized = normalizeEditRow(
          Array.isArray(raw) ? raw[0] : raw,
        );
        if (!normalized) {
          toast.error("Process definition not found.");
          return;
        }
        setEditId(row.id);
        setForm({
          operationname: normalized.operationname,
          workcenter: normalized.workcenter,
        });
        setSelectedOperatorIds(normalized.assigneeUserIds);
        setValidated(false);
        setAddUpdateModal(true);
      });
    } catch (e) {
      console.log(`Error in edit: ${e}`);
      toast.error("Could not load process definition.");
    } finally {
      setBusyLabel("Loading process definitions…");
    }
  };

  const onDelete = async (list: ProcessRow) => {
    const isConfirmed = await confirm();
    if (!isConfirmed) return;

    try {
      const saveOperation = await updateById(ENDPOINT.OPERATIONS_UPDATE, {
        id: list.id,
        operationname: list.operationname,
        workcenter: list.workcenter != null ? Number(list.workcenter) : null,
        active: false,
      });
      if (saveOperation === "success") {
        toast.success("Process definition removed.");
        await onLoad(pageIndex, searchText);
      } else {
        toast.error("Could not remove process definition.");
      }
    } catch (e) {
      console.log(`Error in delete: ${e}`);
      toast.error("Could not remove process definition.");
    }
  };

  const onLoad = useCallback(
    async (nextPageIndex: number = 0, overrideSearchText?: string) => {
      try {
        await runWithLoading(async () => {
          const effectiveSearchText =
            overrideSearchText !== undefined ? overrideSearchText : searchText;

          const request = {
            pagesize: pageSize,
            pagenumber: nextPageIndex,
            serachtext: effectiveSearchText,
          };

          const resp = await getList(ENDPOINT.OPERATIONS_LIST, request);
          const items = Array.isArray(resp) ? resp : (resp as { data?: ProcessRow[] })?.data || [];
          const count = Array.isArray(resp)
            ? items.length
            : Number((resp as { count?: number })?.count || 0);

          setOperation(items as ProcessRow[]);
          setTotalCount(count);
          setPageIndex(nextPageIndex);
        });
      } catch (e) {
        console.log(`Error in getOperation: ${e}`);
        toast.error("Could not load process definitions.");
      }
    },
    [searchText, pageSize, runWithLoading],
  );

  useEffect(() => {
    void onLoad();
  }, [onLoad]);

  const scheduleSearchReload = useCallback(
    (value: string) => {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = window.setTimeout(() => {
        void onLoad(0, value);
      }, 320);
    },
    [onLoad],
  );

  useEffect(() => {
    return () => {
      window.clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const totalPages = Math.ceil(totalCount / pageSize);
  const showPagination = totalPages > 1;
  const hasRecords = operationCollection?.length > 0;

  const saveDisabled =
    !formInputs.operationname.trim() ||
    !String(formInputs.workcenter).trim();

  const selectedMachine = workCenterList.find(
    (w) => String(w.id) === String(formInputs.workcenter),
  );
  const selectedMachineModel =
    (selectedMachine?.catalogModelName ?? "").trim() ||
    (selectedMachine?.machineTypeName ?? "").trim() ||
    "";
  const selectedMachineTechnology =
    (selectedMachine?.catalogTechnologyName ?? "").trim() ||
    (selectedMachine?.machineTypeName ?? "").trim() ||
    "";
  const selectedMachineBrand = (selectedMachine?.catalogBrandName ?? "").trim();

  return (
    <div className="process-definitions-page">
      {isLoading && <AppLoader text={busyLabel} />}
      <div className="procdef-catalog">
          <div className="procdef-catalog__shell">
            <div className="procdef-catalog__head">
              <div>
                <p className="procdef-catalog__subtitle">
                  Atomic operations — the building blocks that compose Workflow Templates.
                </p>
                <nav className="procdef-catalog__crumbs" aria-label="Planning hierarchy">
                  <span className="procdef-catalog__crumb procdef-catalog__crumb--active">
                    <i className="fa fa-cog" aria-hidden />
                    Process Definitions
                  </span>
                  <span className="procdef-catalog__crumb-sep" aria-hidden>
                    &gt;
                  </span>
                  <Link className="procdef-catalog__crumb" to="/mes-workflow">
                    <i className="fa fa-sitemap" aria-hidden />
                    Workflow Templates
                  </Link>
                  <span className="procdef-catalog__crumb-sep" aria-hidden>
                    &gt;
                  </span>
                  <Link className="procdef-catalog__crumb" to="/mes-trurecipe/library">
                    <i className="fa fa-flask" aria-hidden />
                    Recipes
                  </Link>
                  <span className="procdef-catalog__crumb-sep" aria-hidden>
                    &gt;
                  </span>
                  <Link className="procdef-catalog__crumb" to="/mes-jobs">
                    <i className="fa fa-wrench" aria-hidden />
                    Jobs
                  </Link>
                </nav>
              </div>
              <button
                type="button"
                className="procdef-catalog__add-btn"
                onClick={handleShowCreate}
              >
                <i className="fa fa-plus" aria-hidden />
                Add Process
              </button>
            </div>

            <div className="procdef-catalog__search-wrap">
              <i className="fa fa-search procdef-catalog__search-icon" aria-hidden />
              <input
                type="search"
                placeholder="Search Processes"
                className="procdef-catalog__search-input"
                value={searchText}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchText(v);
                  scheduleSearchReload(v);
                }}
                aria-label="Search processes"
              />
            </div>

            {!hasRecords ? (
              <MesEmptyState
                icon="fa-cogs"
                title="No process definitions"
                subtitle="Create a process linked to a machine and operators."
              />
            ) : (
              <div className="procdef-catalog__table-wrap">
                <Table borderless className="procdef-catalog__table mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Machine</th>
                      <th>Mfg. technology</th>
                      <th>Used in</th>
                      <th>Operators</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operationCollection.map((row) => {
                      const wf = row.workflowUsages ?? [];
                      const wfCount = row.workflowCount ?? wf.length;
                      const mfg =
                        (row.mfgTechnologyName && String(row.mfgTechnologyName).trim()) ||
                        (row.catalogTechnologyName && String(row.catalogTechnologyName).trim()) ||
                        (row.machineTypeName && String(row.machineTypeName).trim()) ||
                        "—";
                      const machineName = (row.workcentername ?? "").trim() || "—";
                      const machineCat = formatMachineCatalogSubtitle(row);
                      const iot = (row.iotdevicecode ?? "").trim() || "—";
                      const opsText = (row.operatorNames ?? "").trim() || "—";
                      const nameExpanded = expandedNameRowIds.has(row.id);
                      const detailsPanelId = `procdef-name-details-${row.id}`;
                      const summaryLabelId = `procdef-name-summary-${row.id}`;

                      return (
                        <tr key={row.id}>
                          <td className="procdef-catalog__name-cell">
                            <button
                              type="button"
                              className="procdef-catalog__name-toggle"
                              aria-expanded={nameExpanded}
                              aria-controls={detailsPanelId}
                              id={summaryLabelId}
                              title={nameExpanded ? "Hide details" : "Show details"}
                              onClick={() =>
                                setExpandedNameRowIds((prev) =>
                                  toggleIdInSet(prev, row.id),
                                )
                              }
                            >
                              <span className="procdef-catalog__name-toggle-chevron" aria-hidden>
                                <i
                                  className={`fa ${
                                    nameExpanded ? "fa-chevron-down" : "fa-chevron-right"
                                  }`}
                                />
                              </span>
                              <span className="procdef-catalog__name-toggle-text">
                                <span className="procdef-catalog__process-title">
                                  {row.operationname ?? "—"}
                                </span>
                                <span className="procdef-catalog__used-label procdef-catalog__used-label--block">
                                  {wfCount > 0 ? (
                                    <>
                                      Used in {wfCount}{" "}
                                      {wfCount === 1 ? "workflow" : "workflows"}
                                      .
                                    </>
                                  ) : (
                                    <>Not used in any workflow yet.</>
                                  )}
                                </span>
                              </span>
                            </button>
                            {nameExpanded ? (
                              <div
                                id={detailsPanelId}
                                className="procdef-catalog__name-expanded"
                                role="region"
                                aria-labelledby={summaryLabelId}
                              >
                                {wfCount > 0 ? (
                                  <>
                                    <div className="procdef-catalog__used-label procdef-catalog__used-label--expanded-head">
                                      Used in {wfCount}{" "}
                                      {wfCount === 1 ? "workflow" : "workflow(s)"}:
                                    </div>
                                    <ol className="procdef-catalog__wf-list">
                                      {wf.map((w, idx) => (
                                        <li key={`${row.id}-wf-${w.id}-${idx}`}>
                                          {w.workflowname}
                                        </li>
                                      ))}
                                    </ol>
                                  </>
                                ) : null}
                                <hr className="procdef-catalog__name-sep" />
                                <div className="procdef-catalog__machine-detail-head">
                                  Machine Details:
                                </div>
                                <div className="procdef-catalog__machine-detail-line">
                                  <i className="fa fa-microchip" aria-hidden />
                                  <span>
                                    {machineName}
                                    {machineCat && machineCat !== "—"
                                      ? ` — ${machineCat}`
                                      : ""}
                                  </span>
                                </div>
                                <div className="procdef-catalog__iot-line">IoT: {iot}</div>
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <div className="procdef-catalog__machine-primary">{machineName}</div>
                            <div className="procdef-catalog__machine-secondary">{machineCat}</div>
                          </td>
                          <td>
                            <span className="procdef-catalog__tech-link">{mfg}</span>
                          </td>
                          <td>
                            <span className="procdef-catalog__used-pill">
                              {wfCount} {wfCount === 1 ? "workflow" : "workflows"}
                            </span>
                          </td>
                          <td className="procdef-catalog__operators-cell">{opsText}</td>
                          <td>
                            <div className="procdef-catalog__actions">
                              <button
                                type="button"
                                className="procdef-catalog__action-btn"
                                aria-label="Edit"
                                onClick={() => void onEdit(row)}
                              >
                                <i className="fa fa-pencil" aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="procdef-catalog__action-btn procdef-catalog__action-btn--danger"
                                aria-label="Delete"
                                onClick={() => void onDelete(row)}
                              >
                                <i className="fa fa-trash-o" aria-hidden />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}

            {showPagination && (
              <div className="procdef-catalog__pagination">
                <nav aria-label="Process definitions pagination">
                  <ul className="pagination mb-0">
                    <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
                      <button
                        type="button"
                        className="page-link"
                        disabled={pageIndex === 0}
                        onClick={() => void onLoad(pageIndex - 1)}
                      >
                        &laquo;
                      </button>
                    </li>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <li
                        key={i}
                        className={`page-item ${i === pageIndex ? "active" : ""}`}
                      >
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => void onLoad(i)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        pageIndex >= totalPages - 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        disabled={pageIndex >= totalPages - 1}
                        onClick={() => void onLoad(pageIndex + 1)}
                      >
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>

      <Modal
        className="procdef-modal"
        size="lg"
        show={openAddUpdateModal}
        onHide={handleClose}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={false} className="procdef-modal__header">
          <div>
            <Modal.Title as="h2" className="procdef-modal__title">
              {editId === "" ? "Create Process Definition" : "Edit Process Definition"}
            </Modal.Title>
            <p className="procdef-modal__subtitle">
              Define an atomic manufacturing operation linked to a machine and operators.
            </p>
          </div>
          <button
            type="button"
            className="procdef-modal__close"
            aria-label="Close"
            onClick={handleClose}
          >
            <i className="fa fa-times" aria-hidden />
          </button>
        </Modal.Header>
        <Modal.Body className="procdef-modal__body">
          <Form noValidate validated={validated} onSubmit={onSave} id="procdef-form">
            <Form.Group className="mb-3 procdef-modal__field-group" controlId="operationname">
              <Form.Label className="procdef-modal__field-label">Name</Form.Label>
              <Form.Control
                required
                type="text"
                name="operationname"
                className="procdef-modal__text-input"
                placeholder="e.g. CNC Rough Turning"
                value={formInputs.operationname}
                onChange={(e) => handleFieldChange("operationname", e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                Name is required.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3 procdef-modal__field-group" controlId="workcenter">
              <Form.Label className="procdef-modal__field-label">Machine</Form.Label>
              <Form.Control
                as="select"
                required
                name="workcenter"
                className="form-control custom-select procdef-modal__select"
                value={formInputs.workcenter}
                onChange={(e) => handleFieldChange("workcenter", e.target.value)}
                aria-label="Machine"
              >
                <option value="">Select machine from Resources</option>
                {workCenterList.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.workcentername ?? `Machine #${w.id}`}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                Select a machine.
              </Form.Control.Feedback>
              {selectedMachine ? (
                <div className="procdef-modal__machine-preview">
                  <div className="procdef-modal__machine-preview-row">
                    <span className="procdef-modal__machine-preview-label">Mfg. technology</span>
                    <span>{selectedMachineTechnology || "—"}</span>
                  </div>
                  <div className="procdef-modal__machine-preview-row">
                    <span className="procdef-modal__machine-preview-label">Brand</span>
                    <span>{selectedMachineBrand || "—"}</span>
                  </div>
                  <div className="procdef-modal__machine-preview-row">
                    <span className="procdef-modal__machine-preview-label">Model</span>
                    <span>{selectedMachineModel || "—"}</span>
                  </div>
                </div>
              ) : null}
            </Form.Group>

            <Form.Group className="mb-2 procdef-modal__field-group" controlId="operators">
              <Form.Label className="procdef-modal__field-label">Operators</Form.Label>
              <div className="procdef-modal__operators-block">
                <div className="procdef-modal__operators-scroll">
                  {operatorList.length === 0 ? (
                    <p className="text-muted small mb-0">No operators available.</p>
                  ) : (
                    operatorList.map((op) => {
                      const checked = selectedOperatorIds.includes(op.id);
                      const skills = formatOperatorSkills(op.skills);
                      return (
                        <div
                          key={op.id}
                          className={`procdef-modal__operator-row${
                            checked ? " procdef-modal__operator-row--selected" : ""
                          }`}
                        >
                          <Form.Check
                            type="checkbox"
                            id={`procdef-op-${op.id}`}
                            checked={checked}
                            onChange={() => toggleOperator(op.id)}
                            label={
                              <span>
                                {op.fullname ?? `Operator #${op.id}`}
                                {skills ? (
                                  <span className="procdef-modal__operator-skills">{skills}</span>
                                ) : null}
                              </span>
                            }
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="procdef-modal__footer">
          <Button
            type="button"
            variant="light"
            className="procdef-modal__btn-cancel"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="procdef-form"
            className="procdef-modal__btn-save"
            disabled={saveDisabled}
          >
            {editId === "" ? "Save Process" : "Save changes"}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialog />
    </div>
  );
};

export default MesOperation;


